<template>
  <simple-activity>
    <template v-slot:primaryButton>
      <div role="group" aria-label="Painting options" class="btn-group d-flex justify-content-start align-items-center my-1">
        <button
            class="btn btn-md "
            :class="{'btn-secondary': lineWidth === 5, 'btn-outline-secondary': lineWidth !== 5}"
            @click="setLineWidth(5)"
        style="width: 42px">
          <font-awesome-icon icon="circle" size="xs" />
        </button>
        <button
            class="btn btn-md "
            :class="{'btn-secondary': lineWidth === 15, 'btn-outline-secondary': lineWidth !== 15}"
            @click="setLineWidth(15)"
            style="width: 42px"
            data-test-path="paint-size">
          <font-awesome-icon icon="circle" />
        </button>
        <button
            class="btn btn-md "
            :class="{'btn-secondary': lineWidth === 40, 'btn-outline-secondary': lineWidth !== 40}"
            @click="setLineWidth(40)"
            style="width: 42px">
          <font-awesome-icon icon="circle" size="lg" style="position: relative; right: 2px; top: 1px;" />
        </button>
        <button class="btn btn-md btn-outline-secondary" @click="undo">
          <font-awesome-icon icon="undo" />
        </button>
      </div>
    </template>
    <template v-slot:toolButtons>
      <button class="btn btn-md ml-1 btn-outline-secondary" @click="zoomIn">
        <font-awesome-icon :icon="['far', 'search-plus']" />
      </button>
      <button class="btn btn-md btn-outline-secondary" @click="zoomOut" data-test-path="zoom">
        <font-awesome-icon :icon="['far', 'search-minus']" />
      </button>
      <button class="btn btn-md btn-outline-secondary" @click="resetZoom">
        <font-awesome-icon :icon="['far', 'undo']" />
      </button>
    </template>
    <template v-slot:dangerButton>
      <button class="btn btn-md btn-outline-secondary my-1 ml-1" @click="showTooltip" data-test-path="show-tooltip">
        <font-awesome-icon :icon="['far', 'circle-info']" />
      </button>
    </template>
    <div v-if="!loading" class="d-flex flex-column align-items-start justify-content-start ml-1 inpaint-container">
      <v-box class="d-flex flex-column justify-content-center align-items-center h-100 w-100" :scrollable="true">
        <div ref="canvasContainer" style="position: relative; border-radius: 10px;">
          <div class="zoom-container"
               @mousedown="startPanning"
               @mousemove="pan"
               @mouseup="stopPanning"
               @mouseleave="stopPanning">
            <div
                ref="zoomableArea"
                class="zoomable"
                :style="{ transform: `scale(${zoomLevel}) translate(${panOffsetX / zoomLevel}px, ${panOffsetY / zoomLevel}px)` }">
              <img :src="imageSrc" ref="backgroundImage" @load="onImageLoad" style="width: 100%; max-height: 64vh; border-radius: 10px" />
              <canvas
                  ref="drawingCanvas"
                  :width="canvasWidth"
                  :height="canvasHeight"
                  style="position: absolute; top: 0; left: 0; z-index: 10; cursor: crosshair; max-height: 64vh; width: 100%"
                  @mousedown.stop="startDrawing"
                  @mousemove="draw"
                  @mouseup="stopDrawing"
                  @touchmove="drawTouch"
                  @mouseleave="stopDrawing"
              ></canvas>
              <p class="text-center">Zoom level: {{zoomLevel.toString().slice(0,3)}}x</p>
            </div>
          </div>
        </div>
        <div class="d-flex justify-content-center textarea-container" style="position: relative; display: inline-block; bottom: 2%">
          <b-form-textarea
              v-if="showPrompt"
              v-model="description"
              data-test-path="prompt"
              class="mb-2 textarea"
              :placeholder="$tr('inpaintPrompt', 'Write a prompt to replace the painted part')"
              rows="3"
              style="width: 50%; border-radius: 5px; z-index: 5000; min-width: 500px; min-height: 100px; position: absolute; resize: none">
          </b-form-textarea>
          <button
              v-if="description"
              @click="description = ''"
              class="promptClean text-secondary"
              style="position: absolute; top: 2px; left: 220px; background: transparent; border: none; cursor: pointer; z-index: 5001; font-size: 16px">
            &#x2715;
          </button>
        </div>

      </v-box>
    </div>
    <b-container class="d-flex justify-content-center align-items-center text-center" v-else>
      <div>
        <h3>{{$tr('generating', 'Generating')}}</h3>
        <font-awesome-icon
            :icon="['fad', 'spinner-third']"
            spin
            size="2xl"
            style="--fa-primary-color: #3584e4; --fa-secondary-color: #3584e4;"
            class="ml-4 mt-2"/>
      </div>
    </b-container>
    <template v-slot:footer>
      <footer-button type="Details" :disabled="!description" v-if="showPrompt" icon="hand-pointer" :label="$tr('retouch', 'Retouch')" @details="select"></footer-button>
      <b-button class="btn btn-md" style="margin-left: 15px"
              v-if="!showPrompt"
              variant="plm-tertiary"
              @click="showPrompt = !showPrompt"
              data-test-path="write-prompt">
        <font-awesome-icon :icon="['fad', 'pencil']" /><span class="promptText">{{$tr('writePrompt', 'Write a prompt')}}</span>
      </b-button>
      <b-button class="btn btn-md" style="margin-left: 15px"
              v-if="showPrompt"
              @click="cancel"
              variant="plm-tertiary"
              data-test-path="close-prompt">
        <font-awesome-icon :icon="['fad', 'square-dashed']" /><span class="promptText">{{$tr('toRetouch', 'Back to sketch')}}</span>
      </b-button>
    </template>
  </simple-activity>
</template>

<script>
import SimpleActivity from "@/activity/templates/layouts/SimpleActivity.vue";
import Activity from "@/activity/Activity";
import ActivityManager from "@/activity/ActivityManager";
import FooterButton from "@/components/navbar/FooterButton.vue";
import DialogManager from "@/utils/DialogManager";
import VBox from "@/components/VBox.vue";

export default {
  name: 'AISketch',
  components: {VBox, FooterButton, SimpleActivity},
  props: {
    descriptionTask: { default: null },
    imageSrc: {
      type: String,
      required: true
    },
  },
  data() {
    return {
      isDrawing: false,
      canvasWidth: 0,
      canvasHeight: 0,
      ctx: null,
      penColor: "red",
      lineWidth: 40,
      description: "",
      lastX: null,
      lastY: null,
      undoStack: [],
      loading: false,
      showPrompt: false,
      zoomLevel: 1,
      isPanning: false,
      panOffsetX: 0,
      panOffsetY: 0,
      startPanX: 0,
      startPanY: 0,
      previousScrollPosition: 0,
    };
  },
  mounted() {
    const canvas = this.$refs.drawingCanvas;

    canvas.addEventListener("touchstart", this.startDrawingTouch);
    canvas.addEventListener("touchmove", this.drawTouch);
    canvas.addEventListener("touchend", this.stopDrawing);
  },
  methods: {
    ...Activity.methods,
    getTitle() {
      return 'AI Retouch';
    },
    onImageLoad() {
      const img = this.$refs.backgroundImage;
      this.canvasWidth = img.width;
      this.canvasHeight = img.height;

      const canvas = this.$refs.drawingCanvas;
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      this.ctx = canvas.getContext("2d");
      this.ctx.lineWidth = this.lineWidth;
      this.ctx.strokeStyle = this.penColor;
      this.ctx.lineCap = "round";
      this.updateCanvasSettings();
    },
    zoomIn() {
      this.zoomLevel = Math.min(this.zoomLevel + 0.1, 3);
    },
    zoomOut() {
      this.zoomLevel = Math.max(this.zoomLevel - 0.1, 0.5);
    },
    resetZoom() {
      this.zoomLevel = 1;
      this.panOffsetX = 0;
      this.panOffsetY = 0;
    },
    startDrawingTouch(event) {
      if (event && typeof event.preventDefault === "function") {
        event.preventDefault();
      }
      this.disableScrolling();
      document.addEventListener("touchmove", this.preventScroll, { passive: false });
      const touch = event.touches[0];
      const rect = this.$refs.drawingCanvas.getBoundingClientRect();

      this.startDrawing({
        clientX: touch.clientX,
        clientY: touch.clientY,
        offsetX: touch.clientX - rect.left,
        offsetY: touch.clientY - rect.top,
      });
    },
    drawTouch(event) {
      if (event && typeof event.preventDefault === "function") {
        event.preventDefault();
      }
      const touch = event.touches[0];
      const canvas = this.$refs.drawingCanvas;
      const rect = canvas.getBoundingClientRect();

      this.draw({
        clientX: touch.clientX,
        clientY: touch.clientY,
        offsetX: touch.clientX - rect.left,
        offsetY: touch.clientY - rect.top,
      });
    },
    preventScroll(event) {
      event.preventDefault();
    },
    startPanning(event) {
      this.isPanning = true;
      this.startPanX = event.clientX - this.panOffsetX;
      this.startPanY = event.clientY - this.panOffsetY;
    },
    pan(event) {
      if (!this.isPanning) return;
      this.panOffsetX = event.clientX - this.startPanX;
      this.panOffsetY = event.clientY - this.startPanY;
    },
    stopPanning() {
      this.isPanning = false;
    },
    updateCanvasSettings() {
      if (this.ctx) {
        this.ctx.lineWidth = this.lineWidth;
        this.ctx.strokeStyle = this.penColor;
        this.ctx.lineCap = "round";
      }
    },
    setLineWidth(value) {
      this.lineWidth = value;
    },
    startDrawing(event) {
      if (event && typeof event.preventDefault === "function") {
        event.preventDefault();
      }
      this.disableScrolling();
      document.addEventListener("wheel", this.preventScroll, { passive: false });
      document.addEventListener("touchmove", this.preventScroll, { passive: false });
      this.isDrawing = true;

      const canvas = this.$refs.drawingCanvas;
      // Zapisz stan przed rozpoczęciem rysowania
      this.undoStack.push(this.ctx.getImageData(0, 0, canvas.width, canvas.height));

      const rect = canvas.getBoundingClientRect();
      const offsetX = (event.clientX - rect.left) * (canvas.width / rect.width);
      const offsetY = (event.clientY - rect.top) * (canvas.height / rect.height);

      this.ctx.beginPath();
      this.ctx.arc(offsetX, offsetY, this.lineWidth / 2, 0, Math.PI * 2);
      this.ctx.fillStyle = this.penColor;
      this.ctx.fill();
      this.ctx.closePath();

      this.lastX = offsetX;
      this.lastY = offsetY;
    },
    undo() {
      if (this.undoStack.length > 0) {
        const canvas = this.$refs.drawingCanvas;
        const previousState = this.undoStack.pop();
        this.ctx.putImageData(previousState, 0, 0);
      }
    },
    draw(event) {
      if (event && typeof event.preventDefault === "function") {
        event.preventDefault();
      }
      if (!this.isDrawing) return;

      const canvas = this.$refs.drawingCanvas;
      const rect = canvas.getBoundingClientRect();
      const offsetX = (event.clientX - rect.left) * (canvas.width / rect.width);
      const offsetY = (event.clientY - rect.top) * (canvas.height / rect.height);

      const distance = Math.sqrt((offsetX - this.lastX) ** 2 + (offsetY - this.lastY) ** 2);
      const steps = Math.ceil(distance / (this.lineWidth / 2));

      for (let i = 1; i <= steps; i++) {
        const intermediateX = this.lastX + (i * (offsetX - this.lastX)) / steps;
        const intermediateY = this.lastY + (i * (offsetY - this.lastY)) / steps;

        this.ctx.beginPath();
        this.ctx.arc(intermediateX, intermediateY, this.lineWidth / 2, 0, Math.PI * 2);
        this.ctx.fillStyle = this.penColor;
        this.ctx.fill();
        this.ctx.closePath();
      }

      this.lastX = offsetX;
      this.lastY = offsetY;
    },
    stopDrawing() {
      this.isDrawing = false;
      this.lastX = null;
      this.lastY = null;
      document.removeEventListener("wheel", this.preventScroll);
      document.removeEventListener("touchmove", this.preventScroll);
      this.enableScrolling()
    },
    disableScrolling() {
      this.previousScrollPosition = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.previousScrollPosition}px`;
      document.body.style.width = '100%';
    },

      enableScrolling() {
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';

        window.scrollTo(0, this.previousScrollPosition);
      },
    getMask() {
      return this.$refs.drawingCanvas.toDataURL("image/png");
    },
    async showTooltip() {
      await DialogManager.infoShort($tr('inpaintTitle', 'Paint over the areas you want to change and write a prompt to replace the painted part'),
          $tr('inpaint-instructions', "Make sure to completely cover the parts you want to modify - any unpainted areas will remain unchanged. The paint doesn't need to be precise at the edges, but try to stay within the general boundaries of what you want to replace."))
    },
    async select() {
      if (this.description) {
        this.setResult({
          mask: this.getMask(),
          prompt: this.description,
        });
        this.close();
      }
    },

    cancel() {
      this.showPrompt = !this.showPrompt
      this.description = "";
    },
  },
};
</script>

<style scoped>
.zoom-container {
  overflow: visible;
  position: relative;
}

.promptText {
  margin-left: 5px;
}

.zoomable {
  transform-origin: top center;
  transition: transform 0.3s ease;
  border-radius: 10px;
}

@media screen and (max-width: 1020px) {
  .inpaint-container {
    flex-direction: column;
  }
}

@media screen and (max-width: 790px) {
  .promptText {
    display: none;
  }
}
@media screen and (max-width: 510px) {
  .textarea {
    min-width: 300px !important;
  }
  .promptClean {
    left: 120px !important;
  }
  .zoomable img, .zoomable canvas {
    max-height: 40vh !important;
  }
}

@media screen and (max-height: 850px){
  .zoomable img, .zoomable canvas {
    max-height: 40vh !important;
  }
}

@media screen and (max-height: 762px){
  .zoomable img, .zoomable canvas {
    max-height: 37vh !important;
  }
}

@media screen and (max-height: 732px){
  .zoomable img, .zoomable canvas {
    max-height: 34vh !important;
  }
}
</style>
